import { isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import { getFollowedSellerIds } from '@/redux/modules/followSeller';
import { fetchHomeData } from '@/redux/modules/home/home.actions';
import { fetchItemSummariesIfNeeded } from '@/redux/modules/item/summary/itemSummary.actions';
import { fetchRecentItemsIfNeeded, getLimitedSortedRecentItemIdsFromState } from '@/redux/modules/recentItem';
import { fetchRecommendedItemIfNeeded, getSortedRecommendedItemIdsFromState } from '@/redux/modules/recommendedItem';
import {
    fetchSavedSearchesIfNeeded,
    fetchSavedSearchItemsIfNeeded,
    getSavedSearchItemIds,
    hasSavedSearchesSelector,
} from '@/redux/modules/savedSearch';
import { fetchSellerUpcomingCatalogsIfNeeded } from '@/redux/modules/sellerUpcomingCatalogs';
import { fetchUpcomingItemsIfNeeded } from '@/redux/modules/upcoming/items/upcomingItems.actions';
import { getUpcomingItemIds } from '@/redux/modules/upcoming/items/upcomingItems.selectors';
import { DataLoaderParams } from '@/types/DataLoaderParams';
import { AmplitudeExperiments, AmplitudeVariants } from '@/utils/amplitude/experiments/AmplitudeExperiments';

export default ({ amplitudeExperimentFlags, store }: DataLoaderParams) => {
    const { dispatch, getState } = store;
    const state = getState();
    const hasSavedSearches = hasSavedSearchesSelector(state);
    const followedSellerIds = getFollowedSellerIds(state);

    // Experiment: SEAR-1266 Average Lot Value Auction Calendar Ranking
    const averageLotValueActionCalendarRankingVariant =
        amplitudeExperimentFlags[AmplitudeExperiments.AverageLotValueActionCalendarRanking]?.value;
    const hasAverageLotValueActionCalendarRanking =
        averageLotValueActionCalendarRankingVariant ===
        AmplitudeVariants.AverageLotValueActionCalendarRanking.Treatment;

    return Promise.all([
        dispatch(
            fetchHomeData({
                forceUpdate: false,
                shouldUseAverageLotValueActionCalendarRanking: hasAverageLotValueActionCalendarRanking,
            })
        ),
        dispatch(fetchUpcomingItemsIfNeeded()),
        dispatch(fetchSavedSearchesIfNeeded()),
        dispatch(fetchSavedSearchItemsIfNeeded(hasSavedSearches)),
        dispatch(fetchRecommendedItemIfNeeded(false)),
        ...followedSellerIds.map((sellerId) => dispatch(fetchSellerUpcomingCatalogsIfNeeded(sellerId))),
    ]).then(() => {
        const newState = getState();
        const isLoggedIn = isUserLoggedIn(newState);
        const promises = [];

        if (isLoggedIn) {
            const upcomingItemIds = getUpcomingItemIds(newState);
            const savedSearchItemIds = getSavedSearchItemIds(newState);
            const recommendedItemIds = getSortedRecommendedItemIdsFromState(newState, 12);
            promises.push(
                dispatch(
                    fetchItemSummariesIfNeeded({
                        identifier: 'COMING-UP-Carousel',
                        itemIds: upcomingItemIds,
                    })
                )
            );
            promises.push(
                dispatch(
                    fetchItemSummariesIfNeeded({
                        identifier: 'FOLLOWED-SEARCH-Carousel',
                        itemIds: savedSearchItemIds,
                    })
                )
            );
            promises.push(
                dispatch(
                    fetchItemSummariesIfNeeded({
                        identifier: 'RECOMMENDED-ITEM-Carousel',
                        itemIds: recommendedItemIds,
                    })
                )
            );

            // Fetch recent items and their summaries for SSR
            promises.push(
                dispatch(fetchRecentItemsIfNeeded()).then(() => {
                    const stateAfterRecentItems = getState();
                    const recentItemIds = getLimitedSortedRecentItemIdsFromState(stateAfterRecentItems, 12);
                    if (recentItemIds.length > 0) {
                        return dispatch(
                            fetchItemSummariesIfNeeded({
                                identifier: 'RECENT-ITEM-Carousel',
                                itemIds: recentItemIds,
                            })
                        );
                    }
                })
            );
        }

        return Promise.all(promises);
    });
};
